import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { connectMetaMask, connectCoinbase, disconnectCoinbase } from '../lib/wallet';

const WalletContext = createContext(null);

export const WalletProvider = ({ children }) => {
  const [wallet, setWallet] = useState(null); // { address, chainId, walletType, provider, rawProvider }
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);

  const connect = useCallback(async (kind) => {
    setConnecting(true);
    setError(null);
    try {
      const w = kind === 'coinbase' ? await connectCoinbase() : await connectMetaMask();
      setWallet(w);
      localStorage.setItem('cf_last_wallet', kind);
      return w;
    } catch (e) {
      setError(e?.message || 'Failed to connect wallet');
      throw e;
    } finally {
      setConnecting(false);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (wallet?.walletType === 'coinbase') disconnectCoinbase();
    setWallet(null);
    localStorage.removeItem('cf_last_wallet');
  }, [wallet]);

  // Listen for account / chain changes on injected provider
  useEffect(() => {
    if (!wallet?.rawProvider) return;
    const eth = wallet.rawProvider;
    const handleAccounts = (accounts) => {
      if (!accounts || accounts.length === 0) {
        setWallet(null);
      } else {
        setWallet((prev) => (prev ? { ...prev, address: accounts[0] } : prev));
      }
    };
    // Some wallets emit chainChanged as a hex string ("0x2105"), others as a
    // decimal number (8453), and a few misbehave with both. Rather than trust
    // the event payload, ask the provider for the current network — this
    // avoids `parseInt(8453, 16) === 33875` garbage chainIds that made the app
    // think every chain except the initially-connected one was "unsupported".
    const handleChain = async () => {
      try {
        const provider = new ethers.BrowserProvider(eth);
        const network = await provider.getNetwork();
        const chainId = Number(network.chainId);
        setWallet((prev) => (prev ? { ...prev, chainId, provider } : prev));
      } catch (_) { /* transient — next event will resync */ }
    };
    eth.on && eth.on('accountsChanged', handleAccounts);
    eth.on && eth.on('chainChanged', handleChain);
    return () => {
      eth.removeListener && eth.removeListener('accountsChanged', handleAccounts);
      eth.removeListener && eth.removeListener('chainChanged', handleChain);
    };
  }, [wallet?.rawProvider]);

  return (
    <WalletContext.Provider value={{ wallet, connect, disconnect, connecting, error }}>
      {children}
    </WalletContext.Provider>
  );
};

export const useWallet = () => useContext(WalletContext);
